/**
 * useMachineScheduleFilters.ts
 * Phase 2, Item 10 — URL-sticky filters for the Machine Schedule view.
 * Phase 4, Item 4  — `focusBookingKey` URL param: when set on mount, the
 *                     MachineSchedulePage scrolls + flashes the matching bar.
 *
 * FEAT-2026-0007 6E.1 — removed quoteKey / workOrderKey filter fields and
 *                       activeQuoteKey / activeWorkOrderKey return values.
 *                       The quote/WO entry point is superseded by Scenario
 *                       Planning; the "Edit tasks" button is gone.
 *
 * Responsibilities:
 *   - Serialize / parse filter state to / from URL query params.
 *   - Expose a React hook (`useMachineScheduleFilters`) that wires up
 *     `useSearchParams` from react-router-dom.
 *   - Read `focusBookingKey` from the URL for jump-to behaviour.
 *
 * The pure serialize / parse functions are exported separately so they can be
 * unit-tested without a DOM or router environment.
 *
 * URL param schema:
 *   labSection       — comma-separated LabSection values  e.g. "Rock,Triaxial"
 *   machine          — comma-separated machine codes       e.g. "M-01,M-02"
 *   workType         — comma-separated MachineBookingSource e.g. "Quote,Manual"
 *   tentative        — "0" or "1"  (default "1" = show tentative)
 *   focusBookingKey  — a single MachineBookingKey to scroll+flash on mount
 *   q                — free-text search
 *   start            — REQ-04: ISO 'YYYY-MM-DD' start of the visible window
 *   end              — REQ-04: ISO 'YYYY-MM-DD' end of the visible window
 *
 * REQ-04: the date window (start/end) is now driven by free Start/End date
 * pickers (replacing the old Day/Week/2-Weeks/Month preset toggle). The window
 * is URL-sticky so it survives navigation/refresh, mirroring MasterGantt's
 * Start/End + Apply pattern.
 *
 * BUG-2026-0195: with no start/end in the URL the window defaults to the
 * "Next 3 months" quick-range preset (was a fixed 2-week window).
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { LabSection, MachineBookingSource } from '../../../types/schedule';
import { computeDefaultRange } from './dateRangePresets';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MachineScheduleFilterState {
  /** Selected lab sections (empty = all). */
  labSections: LabSection[];
  /** Selected machine codes (empty = all). */
  machineCodes: string[];
  /** Selected booking source types (empty = all). */
  workTypes: MachineBookingSource[];
  /** Whether tentative (quote-sourced) bookings are shown. */
  showTentative: boolean;
  /**
   * Free-text search. Matches across machine code/name/section, task name,
   * event type, source, status, notes, work-order number, quote number, and
   * assignee names. Empty string = no search.
   */
  q: string;
}

export type MachineScheduleFilterSetter = (
  patch: Partial<MachineScheduleFilterState>,
) => void;

// ---------------------------------------------------------------------------
// Pure parse/serialize helpers (exported for test use)
// ---------------------------------------------------------------------------

const PARAM_LAB_SECTION = 'labSection';
const PARAM_MACHINE = 'machine';
const PARAM_WORK_TYPE = 'workType';
const PARAM_TENTATIVE = 'tentative';
/** Phase 4 Item 4: focus-and-flash a specific booking on mount. */
const PARAM_FOCUS_BOOKING = 'focusBookingKey';
/** Free-text search across the visible window. */
const PARAM_SEARCH = 'q';
/** REQ-04: free date-window pickers — ISO 'YYYY-MM-DD' start / end. */
const PARAM_START = 'start';
const PARAM_END = 'end';

/** Whether a value looks like an ISO 'YYYY-MM-DD' date. */
function isIsoDate(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/** Parse a comma-separated param value into an array, stripping empties. */
export function parseCommaSeparated(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Serialize an array to a comma-separated string, or null if empty. */
export function serializeCommaSeparated(values: string[]): string | null {
  if (values.length === 0) return null;
  return values.join(',');
}

/** Parse all filter params from a plain Record<string, string | null>. */
export function parseFilterParams(
  params: Record<string, string | null>,
): MachineScheduleFilterState {
  const labSections = parseCommaSeparated(params[PARAM_LAB_SECTION]) as LabSection[];
  const machineCodes = parseCommaSeparated(params[PARAM_MACHINE]);
  const workTypes = parseCommaSeparated(params[PARAM_WORK_TYPE]) as MachineBookingSource[];
  // tentative defaults to "1" (shown) when absent; "0" = hidden
  const tentativeRaw = params[PARAM_TENTATIVE];
  const showTentative = tentativeRaw === null || tentativeRaw === undefined
    ? true
    : tentativeRaw !== '0';
  // q: free-text search; empty string when absent.
  const q = params[PARAM_SEARCH] ?? '';

  return { labSections, machineCodes, workTypes, showTentative, q };
}

/** Serialize filter state to a Record of param key → value (null = delete param). */
export function serializeFilterParams(
  state: MachineScheduleFilterState,
): Record<string, string | null> {
  return {
    [PARAM_LAB_SECTION]: serializeCommaSeparated(state.labSections),
    [PARAM_MACHINE]: serializeCommaSeparated(state.machineCodes),
    [PARAM_WORK_TYPE]: serializeCommaSeparated(state.workTypes),
    // Omit tentative param entirely when it is the default (true).
    // This keeps the URL clean for the common case.
    [PARAM_TENTATIVE]: state.showTentative ? null : '0',
    // q: omit when empty/whitespace to keep the URL clean.
    [PARAM_SEARCH]: state.q.trim() ? state.q : null,
  };
}

/** Return the empty / default filter state. */
export function defaultFilterState(): MachineScheduleFilterState {
  return {
    labSections: [],
    machineCodes: [],
    workTypes: [],
    showTentative: true,
    q: '',
  };
}

/** Whether the filter state is the default (no active filters). */
export function isFilterDefault(state: MachineScheduleFilterState): boolean {
  return (
    state.labSections.length === 0 &&
    state.machineCodes.length === 0 &&
    state.workTypes.length === 0 &&
    state.showTentative === true &&
    state.q.trim() === ''
  );
}

// ---------------------------------------------------------------------------
// REQ-04 — Date-window (start/end) parse/serialize helpers
// ---------------------------------------------------------------------------

/** The visible date window for the Machine Schedule grid. */
export interface MachineScheduleWindow {
  /** ISO 'YYYY-MM-DD' — start of the visible window. */
  start: string;
  /** ISO 'YYYY-MM-DD' — end of the visible window (inclusive). */
  end: string;
}

/**
 * The default window when the URL has no start/end (and the "Today" reset):
 * the "Next 3 months" quick-range preset (BUG-2026-0195 — was a fixed 14-day
 * window). Sourced from dateRangePresets so the toolbar's Quick-range dropdown
 * recognises the fresh view as that preset.
 */
export function defaultWindow(): MachineScheduleWindow {
  const { startDate, endDate } = computeDefaultRange(new Date());
  return { start: startDate, end: endDate };
}

/**
 * Parse the window from URL params. Falls back to the default window when a
 * param is missing or malformed, and guarantees end >= start (swapping if the
 * user typed them backwards) so the grid always renders a valid range.
 */
export function parseWindowParams(
  params: Record<string, string | null>,
): MachineScheduleWindow {
  const fallback = defaultWindow();
  let start = isIsoDate(params[PARAM_START]) ? params[PARAM_START]! : fallback.start;
  let end = isIsoDate(params[PARAM_END]) ? params[PARAM_END]! : fallback.end;
  if (end < start) [start, end] = [end, start];
  return { start, end };
}

/** Serialize the window to URL params (always written; the window is required). */
export function serializeWindowParams(
  window: MachineScheduleWindow,
): Record<string, string> {
  return { [PARAM_START]: window.start, [PARAM_END]: window.end };
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

export interface UseMachineScheduleFiltersReturn {
  filterState: MachineScheduleFilterState;
  setFilter: MachineScheduleFilterSetter;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  /**
   * Phase 4 Item 4: The booking key to focus on mount (from URL param
   * `focusBookingKey`). Null when not present. Read-only — set from URL only.
   */
  focusBookingKey: string | null;
  /** REQ-04: the URL-sticky visible date window (start/end). */
  window: MachineScheduleWindow;
  /** REQ-04: replace the visible date window (writes `start`/`end` to the URL). */
  setWindow: (window: MachineScheduleWindow) => void;
}

/**
 * Hook that reads filter state from the URL and writes changes back.
 *
 * Uses `useSearchParams` (react-router-dom). All other URL params (e.g. the
 * date range) are preserved on writes.
 */
export function useMachineScheduleFilters(): UseMachineScheduleFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse from URLSearchParams on each render.
  const filterState = useMemo<MachineScheduleFilterState>(() => {
    const raw: Record<string, string | null> = {
      [PARAM_LAB_SECTION]: searchParams.get(PARAM_LAB_SECTION),
      [PARAM_MACHINE]: searchParams.get(PARAM_MACHINE),
      [PARAM_WORK_TYPE]: searchParams.get(PARAM_WORK_TYPE),
      [PARAM_TENTATIVE]: searchParams.get(PARAM_TENTATIVE),
      [PARAM_SEARCH]: searchParams.get(PARAM_SEARCH),
    };
    return parseFilterParams(raw);
  }, [searchParams]);

  const hasActiveFilters = !isFilterDefault(filterState);

  // REQ-04: parse the visible date window (start/end) from the URL.
  const window = useMemo<MachineScheduleWindow>(() => {
    return parseWindowParams({
      [PARAM_START]: searchParams.get(PARAM_START),
      [PARAM_END]: searchParams.get(PARAM_END),
    });
  }, [searchParams]);

  // REQ-04: write a new window to the URL (replace so navigating back doesn't
  // replay every Apply tick). Other params (filters, search) are preserved.
  const setWindow = useCallback(
    (next: MachineScheduleWindow) => {
      const serialized = serializeWindowParams(next);
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(serialized)) {
            updated.set(key, value);
          }
          return updated;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setFilter = useCallback<MachineScheduleFilterSetter>(
    (patch) => {
      const next: MachineScheduleFilterState = { ...filterState, ...patch };
      const serialized = serializeFilterParams(next);

      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(serialized)) {
            if (value === null) {
              updated.delete(key);
            } else {
              updated.set(key, value);
            }
          }
          return updated;
        },
        // Use replace so navigating back doesn't replay every filter tick.
        { replace: true },
      );
    },
    [filterState, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        updated.delete(PARAM_LAB_SECTION);
        updated.delete(PARAM_MACHINE);
        updated.delete(PARAM_WORK_TYPE);
        updated.delete(PARAM_TENTATIVE);
        updated.delete(PARAM_SEARCH);
        return updated;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // ── Phase 4 Item 4: focusBookingKey ──────────────────────────────────────
  // Read from URL; not serialized back (it is a one-shot navigation hint).
  const focusBookingKey = searchParams.get(PARAM_FOCUS_BOOKING);

  return {
    filterState,
    setFilter,
    clearFilters,
    hasActiveFilters,
    focusBookingKey,
    window,
    setWindow,
  };
}

// ---------------------------------------------------------------------------
// Client-side filter predicate
// ---------------------------------------------------------------------------

/**
 * Filter a bookings array client-side based on the current filter state.
 *
 * Rules:
 *   - labSections: if non-empty, only include bookings whose MachineLabSection
 *     is in the selected set. Unknown machine sections ('Other') are treated
 *     as-is — they match only if 'Other' is selected.
 *   - machineCodes: if non-empty, only include bookings whose MachineCode is in
 *     the selected set. Codes that don't appear in the active machine list are
 *     silently ignored by simply not matching any booking.
 *   - workTypes: if non-empty, only include bookings whose Source is in the set.
 *   - showTentative: if false, exclude bookings with Status='Tentative'.
 *
 * This function is pure (no side effects) and can be tested independently.
 */
export interface BookingForFilter {
  MachineBookingKey: string;
  Status: 'Tentative' | 'Committed';
  Source: MachineBookingSource;
  MachineCode: string;
  MachineLabSection: LabSection | null;
}

export function applyMachineScheduleFilters<T extends BookingForFilter>(
  bookings: T[],
  state: MachineScheduleFilterState,
): T[] {
  return bookings.filter((b) => {
    // Tentative filter
    if (!state.showTentative && b.Status === 'Tentative') return false;

    // Lab section filter
    if (state.labSections.length > 0) {
      const section = b.MachineLabSection ?? 'Other';
      if (!state.labSections.includes(section as LabSection)) return false;
    }

    // Machine code filter
    if (state.machineCodes.length > 0) {
      if (!state.machineCodes.includes(b.MachineCode)) return false;
    }

    // Work type / Source filter
    if (state.workTypes.length > 0) {
      if (!state.workTypes.includes(b.Source)) return false;
    }

    return true;
  });
}
